"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { AstroMascot } from "./AstroMascot";
import { useAutosaveContext } from "./AutosaveManager";
import { 
  Save, 
  X, 
  Mail, 
  CheckCircle,
  ArrowRight,
  AlertCircle
} from "lucide-react";

interface ExitIntentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveAndExit: () => void;
  onExitWithoutSaving: () => void;
  currentStep: string;
  stepIndex: number;
  totalSteps: number;
}

export function ExitIntentModal({
  isOpen,
  onClose,
  onSaveAndExit,
  onExitWithoutSaving,
  currentStep,
  stepIndex,
  totalSteps
}: ExitIntentModalProps) {
  const { saveProgress, sendResumeEmail, autosaveState } = useAutosaveContext();
  const [isSaving, setIsSaving] = React.useState(false);
  const [emailSent, setEmailSent] = React.useState(false);
  const [emailSending, setEmailSending] = React.useState(false);

  const handleSaveAndExit = async () => {
    setIsSaving(true);
    
    try {
      // Save current progress
      await saveProgress({
        currentStep,
        stepIndex,
        totalSteps,
        completedSteps: Array.from({ length: stepIndex }, (_, i) => `step_${i + 1}`)
      });
      
      // Send resume email
      setEmailSending(true);
      const emailSuccess = await sendResumeEmail();
      setEmailSent(emailSuccess);
      setEmailSending(false);
      
      // Small delay to show success state
      setTimeout(() => {
        onSaveAndExit();
      }, 1500);
      
    } catch (error) {
      console.error('Error saving progress:', error);
      setIsSaving(false);
      setEmailSending(false);
    }
  };

  // Reset state when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setIsSaving(false);
      setEmailSent(false);
      setEmailSending(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={!isSaving ? onClose : undefined}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative z-10 w-full max-w-md mx-4"
        >
          <Card className="bg-white p-6 shadow-2xl border border-gray-200 relative overflow-hidden">
            {/* Close button */}
            {!isSaving && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </Button>
            )}

            {/* Success State */}
            {emailSent && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                
                <h3 className="text-gray-900 mb-2">Progress Saved!</h3>
                <p className="text-gray-600 text-sm mb-4">
                  We've sent you a secure link to resume your onboarding journey anytime.
                </p>
                
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <p className="text-green-800 text-sm">
                    ✓ Check your email for the resume link
                  </p>
                </div>
              </motion.div>
            )}

            {/* Main Content */}
            {!emailSent && (
              <>
                <div className="text-center mb-6">
                  <h3 className="text-gray-900 mb-2">Taking a break? No worries!</h3>
                  <p className="text-gray-600 text-sm">
                    We'll save your spot and send you a link to continue later.
                  </p>
                </div>

                {/* Astro with Save Icon */}
                <div className="flex items-start space-x-4 mb-6">
                  <motion.div
                    animate={{ 
                      y: isSaving ? [0, -5, 0] : 0,
                      rotate: isSaving ? [0, 3, -3, 0] : 0
                    }}
                    transition={{ 
                      duration: isSaving ? 1 : 0,
                      repeat: isSaving ? Infinity : 0,
                      ease: "easeInOut"
                    }}
                    className="relative"
                  >
                    <AstroMascot expression="helpful" size="md" />
                    {/* Save icon in Astro's hand */}
                    <motion.div
                      className="absolute -bottom-1 -right-1 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg"
                      animate={{ 
                        scale: isSaving ? [1, 1.2, 1] : 1,
                        rotate: isSaving ? 360 : 0
                      }}
                      transition={{ 
                        scale: { duration: 1, repeat: isSaving ? Infinity : 0 },
                        rotate: { duration: 2, repeat: isSaving ? Infinity : 0, ease: "linear" }
                      }}
                    >
                      <Save className="w-4 h-4 text-white" />
                    </motion.div>
                  </motion.div>

                  <div className="flex-1">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 relative">
                      {/* Speech bubble pointer */}
                      <div className="absolute left-0 top-4 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[8px] border-r-blue-50 -translate-x-2" />
                      
                      <p className="text-blue-800 text-sm leading-relaxed">
                        {isSaving ? (
                          "🔄 I'm stashing your progress safely. You can pick up where you left off anytime!"
                        ) : (
                          "💾 I've got your back! Let me save your progress and send you a secure link to continue later."
                        )}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Progress Info */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-gray-600">Current Progress</span>
                    <span className="text-sm text-gray-800">
                      Step {stepIndex + 1} of {totalSteps}
                    </span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
                    <motion.div 
                      className="bg-blue-500 h-2 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-xs text-gray-600">
                      Currently on: {currentStep.replace('astro-', '').replace('-', ' ')}
                    </span>
                  </div>
                </div>

                {/* Email Status */}
                {emailSending && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 flex items-center space-x-3"
                  >
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <Mail className="w-4 h-4 text-blue-600" />
                    </motion.div>
                    <span className="text-blue-800 text-sm">Sending resume link to your email...</span>
                  </motion.div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button
                    onClick={handleSaveAndExit}
                    disabled={isSaving || emailSending}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white relative overflow-hidden"
                  >
                    {isSaving || emailSending ? (
                      <>
                        <motion.div
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          className="w-4 h-4 mr-2"
                        >
                          <Save className="w-4 h-4" />
                        </motion.div>
                        {emailSending ? 'Sending Resume Link...' : 'Saving Progress...'}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Progress & Send Email
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>

                  <Button
                    variant="outline"
                    onClick={onExitWithoutSaving}
                    disabled={isSaving || emailSending}
                    className="w-full text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                  >
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Exit Without Saving
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={onClose}
                    disabled={isSaving || emailSending}
                    className="w-full text-gray-600 hover:text-gray-800"
                  >
                    Continue Onboarding
                  </Button>
                </div>
              </>
            )}
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}